import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';
import { useInviteContinuation } from '../hooks/useInviteContinuation';
import { InviteWelcome } from '../components/InviteWelcome';
import { safeContinuePath } from '../utils/safeContinuePath';
import { SIGNED_IN_LANDING_PATH } from '../utils/signInLanding';

interface SignupResponse {
  ok: boolean;
  user?: {
    id: string;
    email: string;
    name: string;
  };
  workspace?: {
    id: string;
    name: string;
  };
  /** Whether the initial verification email was accepted for delivery. */
  verificationEmail?: { status: 'sent'; expiresAt: string } | { status: 'failed' };
  /**
   * Whether Central established a browser SESSION during signup.
   *
   * Reported by the server rather than inferred, so a session failure
   * degrades to the sign-in redirect instead of stranding the user on a page
   * that believes it is authenticated.
   */
  sessionEstablished?: boolean;
  error?: string;
  code?: string;
}

/**
 * AuthSignup page for user registration.
 * Creates a new user account and workspace.
 * Preserves OAuth params in `continue` query param for OIDC flow resumption.
 */
/**
 * Where a brand-new user goes once signup has established their session.
 *
 * Exported so the regression test asserts the SAME spelling the component uses
 * rather than a copy of it — the defect this replaces was a path that matched
 * no route at all.
 */
export const ONBOARDING_DESTINATION = '/onboarding/new';

export function AuthSignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the continue URL (encoded OAuth params). An off-origin value is
  // dropped here so it can reach neither the login redirect nor the emailed
  // verification link.
  const continueUrl = safeContinuePath(searchParams.get('continue')) ?? '';

  // Invitation context, when the user arrived from an invitation. Same page,
  // same registration path — the invited address is prefilled and locked
  // because the invitation is bound to exactly that identity, and an account
  // created under another one could not accept it. Null for ordinary signups,
  // which keep a free-text email field.
  const inviteContext = useInviteContinuation(continueUrl);
  const invitedEmail = inviteContext?.email ?? '';
  useEffect(() => {
    if (invitedEmail) setEmail(invitedEmail);
  }, [invitedEmail]);

  // Map backend error codes to user-friendly messages
  const getErrorMessage = (code: string | undefined, fallback: string): string => {
    switch (code) {
      case 'WEAK_PASSWORD':
        return 'Password must be at least 10 characters';
      case 'EMAIL_EXISTS':
        return 'An account with this email already exists';
      case 'INVALID_EMAIL':
        return 'Please enter a valid email address';
      default:
        return fallback;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!name.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (password.length < 10) {
      toast.error('Password must be at least 10 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // return_to rides into the verification link, so verifying — even
        // in another browser — resumes this journey (e.g. /start/business).
        body: JSON.stringify({
          email: email.trim(),
          password,
          name: name.trim(),
          // Never an /auth/* continuation: an invite one carries the invite
          // token, which must not be copied into a second email. The server
          // enforces the same rule.
          ...(continueUrl && !continueUrl.startsWith('/auth/') ? { return_to: continueUrl } : {}),
        }),
      });

      const data: SignupResponse = await response.json();

      if (!response.ok) {
        const errorMsg = getErrorMessage(data.code, data.error || 'Signup failed');
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      // NOBODY TYPES THEIR PASSWORD TWICE.
      //
      // `/api/auth/signup` now establishes the Central SESSION server-side,
      // using the same `createSession` + `setSessionCookie` pair
      // `/api/oidc/session/login` uses, and reports `sessionEstablished`.
      //
      // This replaces a credential RE-POST. The previous code worked around
      // the missing session by POSTing the just-typed email and password back
      // to `/api/oidc/session/login` from the browser — and only for invited
      // users; everyone else was sent to the sign-in page to retype a password
      // they had set seconds earlier. The password no longer leaves this
      // handler, and the invite and ordinary paths are now the SAME path,
      // which is why the invite special case is gone rather than duplicated.
      //
      // Guarded on the server's own answer, not on assuming success: if the
      // session could not be established the sign-in redirect below still
      // runs, so the worst case is exactly the behaviour that shipped before.
      if (data.sessionEstablished) {
        toast.success('Account created!');
        // A zero-workspace user has nowhere to land but onboarding, and signup
        // has just created their first workspace — so Central decides the
        // destination. `continueUrl` is already origin-checked by
        // `safeContinuePath`, so it cannot become an open redirect here.
        //
        // THE DESTINATION IS `/onboarding/new`, NOT `/onboarding`.
        //
        // `/onboarding` is not a route. App.tsx registers `/onboarding/new`
        // and `/onboarding/business/*`, so a bare `/onboarding` fell through to
        // the `/*` catch-all, hit `ProtectedRoutes`, and landed the brand-new
        // user on `/login` — the ADMIN JWT paste page. Observed end-to-end on
        // staging: the session cookie was set correctly and the user still
        // ended up staring at an admin login form.
        //
        // `/onboarding/new` is the established convention, not a new one:
        // `/api/oidc/authorize` already redirects a zero-workspace browser
        // there, and `uiShellService` publishes it as `createWorkspaceUrl`.
        //
        // AN APP SIGN-IN CONTINUATION RIDES ALONG THROUGH ONBOARDING. A new
        // account that signed up from an app (`continue=/api/oidc/authorize…`)
        // used to follow the authorize straight back into the app, skipping
        // Central's Welcome and onboarding entirely. It now starts at the
        // Welcome/chooser with the continuation kept, and finishing onboarding
        // hands back to the app (as sign-in already does for unfinished owners).
        window.location.href =
          continueUrl && continueUrl.startsWith('/api/')
            ? `${ONBOARDING_DESTINATION}?continue=${encodeURIComponent(continueUrl)}`
            : continueUrl || ONBOARDING_DESTINATION;
        return;
      }

      // Signup successful. Say a verification email was sent only when the
      // server confirms it was accepted for delivery.
      if (data.verificationEmail?.status === 'sent') {
        toast.success('Account created! We sent a verification link to your email.');
      } else {
        toast.success('Account created! Redirecting to sign in...');
        toast.error("We couldn't send your verification email yet. You can resend it after signing in.");
      }

      // Redirect to login with registered flag
      setTimeout(() => {
        if (continueUrl) {
          navigate(`/auth/login?continue=${encodeURIComponent(continueUrl)}&registered=true`);
        } else {
          navigate('/auth/login?registered=true');
        }
      }, 1000);
    } catch (err) {
      const errorMsg = 'Network error. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Build login URL preserving continue param
  const loginUrl = continueUrl
    ? `/auth/login?continue=${encodeURIComponent(continueUrl)}`
    : '/auth/login';

  return (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        {inviteContext && <InviteWelcome invite={inviteContext} />}

        <div className={inviteContext ? 'mb-6' : 'text-center mb-6'}>
          <h1 className="text-2xl font-semibold text-gray-900">
            {inviteContext ? 'Create your account to join' : 'Create your account'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {inviteContext
              ? 'One step left — set a password and you\'re in.'
              : 'Get started with LevelUp'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Full name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              readOnly={!!invitedEmail}
              disabled={!!invitedEmail}
              aria-describedby={invitedEmail ? 'invited-email-hint' : undefined}
              className={
                invitedEmail
                  ? 'w-full px-3 py-2 border border-gray-200 rounded-md shadow-sm bg-gray-100 text-gray-600 cursor-not-allowed'
                  : 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
              }
              placeholder="you@example.com"
            />
            {invitedEmail && (
              <p id="invited-email-hint" className="mt-1 text-xs text-gray-500">
                Your invitation can only be accepted with this address.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={10}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Min. 10 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-controls="password"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Google Sign-up Button */}
        <button
          type="button"
          onClick={() => {
            // Suite-app OIDC flow: continueUrl is the /api/oidc/authorize URL.
            // Append provider=google so the existing OIDC-driven Google flow runs.
            if (continueUrl.startsWith('/api/oidc/authorize')) {
              const separator = continueUrl.includes('?') ? '&' : '?';
              window.location.href = `${continueUrl}${separator}provider=google`;
              return;
            }
            // Otherwise (no continueUrl, or a frontend route like /start/business):
            // use the backend-initiated session flow. With no continueUrl,
            // return to the sign-in landing, as the sign-in page does: a NEW
            // account is still sent into onboarding by the Google callback, and
            // an existing one lands where any sign-in would (a Central admin on
            // the console, everyone else on /home). Never bare `/`. The backend
            // still validates it.
            const returnTo = continueUrl || SIGNED_IN_LANDING_PATH;
            window.location.href = `/api/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`;
          }}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-md font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign up with Google
        </button>

        {/* Sign in link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to={loginUrl}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Terms notice */}
        <p className="mt-4 text-xs text-gray-500 text-center">
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </p>
        </div>
      </div>
    </div>
  );
}
